import {
  GetOption,
  HateoasResourceOperation,
  HateoasResourceService,
  HttpMethod,
  Include,
  PagedGetOption,
  PagedResourceCollection,
  Resource,
  ResourceCollection
} from "@lagoshny/ngx-hateoas-client";
import { BehaviorSubject, firstValueFrom, Observable } from "rxjs";
import { WebStompEntitySubscriber } from "../webstomp/web-stomp-entity-subscriber.service";
import { EntityChangeType } from "../webstomp/entity-change-event";
import { Injectable } from "@angular/core";

@Injectable()
export class RealTimeDataStore<T extends Resource> {
  protected _entitiesSubject: BehaviorSubject<T[]>;
  protected _entitySubject: BehaviorSubject<T>;
  protected _deletedSubject: BehaviorSubject<T>;
  protected dataStore: {
    entities: T[];
    entity: T;
  };
  private streamSingleUpdates: boolean;

  /* eslint-disable @angular-eslint/prefer-inject -- Abstract base class requires constructor params for super() calls from subclasses */
  constructor(
    protected restService: HateoasResourceOperation<T>,
    protected stompEntitySubscriber: WebStompEntitySubscriber,
    protected hateoasResourceService: HateoasResourceService
  ) {
    /* eslint-enable @angular-eslint/prefer-inject */
    this.dataStore = { entities: [], entity: null };
    this._entitiesSubject = <BehaviorSubject<T[]>>new BehaviorSubject([]);
    this._entitySubject = <BehaviorSubject<T>>new BehaviorSubject(null);
    this._deletedSubject = <BehaviorSubject<T>>new BehaviorSubject(null);
  }

  protected _loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );

  get loading(): Observable<boolean> {
    return this._loading.asObservable();
  }

  get entities(): Observable<T[]> {
    return this._entitiesSubject.asObservable();
  }

  get entity(): Observable<T> {
    return this._entitySubject.asObservable();
  }

  get deleted(): Observable<T> {
    return this._deletedSubject.asObservable();
  }

  advancedSearchObservable(
    params: PagedGetOption,
    searchBody: any[] = []
  ): Observable<PagedResourceCollection<T>> {
    console.log("Performing advanced search ", params, searchBody);
    if ((!searchBody || searchBody.length === 0) && !params.sort) {
      return this.restService.getPage(params);
    }
    // Build request body with criteria and sort (for deep sorting support)
    const requestBody: { criteria: any[]; sort?: Record<string, string> } = {
      criteria: searchBody || []
    };
    // Move sort from URL params to body for deep sort support
    if (params.sort) {
      requestBody.sort = params.sort as Record<string, string>;
    }
    // Remove sort from params since it's now in the body
    const { sort, ...paramsWithoutSort } = params;
    return this.restService.customQuery<PagedResourceCollection<T>>(
      HttpMethod.POST,
      "/search/advanced",
      { body: requestBody, valuesOption: { include: Include.NULL_VALUES } },
      paramsWithoutSort
    );
  }

  advancedSearch(
    params: PagedGetOption,
    searchBody: any[] = []
  ): Promise<PagedResourceCollection<T>> {
    console.log("Performing advanced search ", params, searchBody);
    return new Promise<PagedResourceCollection<T>>((resolve, reject) => {
      if ((!searchBody || searchBody.length === 0) && !params.sort) {
        this.restService.getPage(params).subscribe(
          (value) => {
            resolve(value);
          },
          (error) => reject(error)
        );
      } else {
        // Build request body with criteria and sort (for deep sorting support)
        const requestBody: { criteria: any[]; sort?: Record<string, string> } =
          {
            criteria: searchBody || []
          };
        // Move sort from URL params to body for deep sort support
        if (params.sort) {
          requestBody.sort = params.sort as Record<string, string>;
        }
        // Remove sort from params since it's now in the body
        const { sort, ...paramsWithoutSort } = params;
        this.restService
          .customQuery<PagedResourceCollection<T>>(
            HttpMethod.POST,
            "/search/advanced",
            {
              body: requestBody,
              valuesOption: { include: Include.NULL_VALUES }
            },
            paramsWithoutSort
          )
          .subscribe(
            (value) => {
              resolve(value);
            },
            (error) => {
              reject(error);
            }
          );
      }
    });
  }

  loadAll(
    params: PagedGetOption = {
      pageParams: {
        size: 5000
      }
    },
    detached = false
  ): Promise<PagedResourceCollection<T>> {
    this._loading.next(true);
    return new Promise((resolve, reject) => {
      this.restService.getPage(params).subscribe(
        (entities) => {
          console.log(
            "Paged load complete, detached? " + detached + " entities: ",
            entities
          );
          this._loading.next(false);
          if (!detached) {
            this.dataStore.entities = entities.resources;
            this._entitiesSubject.next(
              Object.assign({}, this.dataStore).entities
            );
          }
          resolve(entities);
        },
        (error) => {
          this._loading.next(false);
          reject(error);
        }
      );
    });
  }

  countWithSearch(
    searchQuery: string,
    options: PagedGetOption
  ): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      this.restService.searchPage(searchQuery, options).subscribe(
        (value) => {
          resolve(value.totalElements);
        },
        (error) => reject(error)
      );
    });
  }

  loadUnpagedSearch(
    searchQuery: string,
    params: GetOption,
    detached = false
  ): Promise<ResourceCollection<T>> {
    return new Promise<any>((resolve, reject) => {
      this.restService.searchCollection(searchQuery, params).subscribe(
        (entities) => {
          resolve(entities);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  loadWithSearch(
    searchQuery: string,
    params: PagedGetOption,
    detached = false
  ): Promise<PagedResourceCollection<T>> {
    this._loading.next(true);
    return new Promise<any>((resolve, reject) => {
      this.restService.searchPage(searchQuery, params).subscribe(
        (entities) => {
          this._loading.next(false);
          if (detached === false) {
            this.dataStore.entities = entities.resources;
            this._entitiesSubject.next(
              Object.assign({}, this.dataStore).entities
            );
          }
          resolve(entities);
        },
        (error) => {
          this._loading.next(false);
          reject(error);
        }
      );
    });
  }

  loadProjectionWithSearch<S extends Resource>(
    type: { new (): S },
    searchQuery: string,
    params: PagedGetOption
  ): Promise<PagedResourceCollection<S>> {
    return firstValueFrom(
      this.hateoasResourceService.searchPage(type, searchQuery, params)
    );
  }

  loadSingleWithSearch(
    searchQuery: string,
    options: GetOption,
    detached = false
  ) {
    this._loading.next(true);
    return new Promise<any>((resolve, reject) => {
      this.restService.searchResource(searchQuery, options).subscribe(
        (entity) => {
          this._loading.next(false);
          if (detached === false) {
            this.dataStore.entity = entity;
            this._entitiesSubject.next(
              Object.assign({}, this.dataStore).entities
            );
          }
          resolve(entity);
        },
        (error) => {
          this._loading.next(true);
          reject(error);
        }
      );
    });
  }

  loadSingleProjection<S extends Resource>(
    type: { new (): S },
    id: number,
    projection: string
  ): Promise<S> {
    return firstValueFrom(
      this.hateoasResourceService.getResource(type, id, {
        params: { projection: projection }
      })
    );
  }

  loadSingle(id: number, detached = false): Promise<T> {
    let query = firstValueFrom(this.restService.getResource(id));
    if (!detached) {
      this._loading.next(true);
      query.then((value) => {
        this.dataStore.entity = value;
        this._entitySubject.next(Object.assign({}, this.dataStore).entity);
        this._loading.next(false);
      });
    }
    return query;
  }

  //
  // loadSingle(
  //   id: number,
  //   options: any = null,
  //   projection: string = null,
  //   detached = false
  // ): Promise<any> {
  //   this._loading.next(true);
  //   if (!options) {
  //     options = {
  //       params: []
  //     };
  //   }
  //   if (projection) {
  //     if (
  //       options.params.filter((value) => value.key === projection)
  //         .length === 0
  //     ) {
  //       options.params.push({
  //         key: "projection",
  //         value: projection
  //       });
  //     }
  //   }
  //   // console.log('loading single ' + id);
  //   // console.log(options);
  //   return new Promise((resolve, reject) => {
  //     this.restService.getResource(id, options.params).subscribe(
  //       entity => {
  //         this._loading.next(false);
  //         if (detached === false) {
  //           this.dataStore.entity = entity;
  //           this._entitySubject.next(Object.assign({}, this.dataStore).entity);
  //         }
  //         resolve(entity);
  //       },
  //       error => {
  //         this._loading.next(false);
  //         reject(error);
  //       }
  //     );
  //   });
  // }

  batchCreate(entity: T[]): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      let promises = [];
      entity.forEach((value: T) => {
        delete value["id"];
        promises.push(
          new Promise((resolve1, reject) => {
            this.restService.createResource({ body: value }).subscribe(
              () => {
                resolve1(null);
              },
              (error) => reject(error)
            );
          })
        );
      });
      Promise.all(promises)
        .then(() => {
          // console.log("all promises resolved");
          resolve(true);
        })
        .catch((reason) => {
          console.log("caught batch create error", reason);
          reject(reason);
        });
    });
  }

  batchUpdate(entity: T[]) {
    console.log("batch updating ", entity);
    entity.forEach((value, index) => {
      console.log("item " + index, JSON.stringify(value));
    });
    return new Promise<boolean>((resolve, reject) => {
      let promises = [];
      entity.forEach((value: T) => {
        promises.push(
          new Promise((resolve1, reject1) => {
            console.log("Preparing to patch with value", value);
            this.restService
              .patchResource(value, {
                body: this.removeLinks<T>(value),
                valuesOption: { include: Include.NULL_VALUES }
              })
              .subscribe(
                () => {
                  resolve1(null);
                },
                (fail) => {
                  reject1(fail);
                }
              );
          })
        );
      });
      // console.log(`created ${promises.length} modification promises`);
      Promise.all(promises)
        .then(() => {
          // console.log("all promises resolved");
          resolve(true);
        })
        .catch((reason) => {
          console.log("caught batch update error", reason);
          reject(reason);
        });
    });
  }

  batchDelete(toRemove: T[]) {
    console.log("batch deleting ", toRemove);
    return new Promise<boolean>((resolve, reject) => {
      let promises = [];
      toRemove.forEach((value: T) => {
        promises.push(
          new Promise((resolve1, reject1) => {
            firstValueFrom(this.restService.deleteResource(value))
              .then((value1) => resolve1(null))
              .catch((reason) => {
                reject1(reason);
              });
          })
        );
      });
      // console.log(`created ${promises.length} remove promises`);
      Promise.all(promises)
        .then(() => {
          console.log("all promises resolved");
          resolve(true);
        })
        .catch((reason) => {
          console.log("caught batch delete error", reason);
          reject(reason);
        });
    });
  }

  create(entity: T) {
    return this.restService.createResource({
      body: entity
    });
  }

  delete(entity: T): Observable<Object> {
    return this.restService.deleteResource(entity) as any as Observable<Object>;
  }

  update(entity: T) {
    return this.restService.updateResource(entity);
  }

  protected handleInsert(changeEvent) {
    // console.log("performing insert lookup");
    // console.log(this.restService);
    this.restService
      // .getBySelfLink(changeEvent.href + '?projection=detail')
      .getResource(changeEvent.id, { params: { projection: "detail" } })
      .subscribe((entity) => {
        this.dataStore.entities.push(entity);
        this._entitiesSubject.next(Object.assign({}, this.dataStore).entities);
      });
  }

  protected handleDelete(changeEvent: any) {
    // console.log("got delete");
    const existingLocalRecord = this.dataStore.entities.find(
      (x) => x["id"] === changeEvent.id
    );
    this.dataStore.entities.splice(
      this.dataStore.entities.indexOf(existingLocalRecord),
      1
    );
    this._deletedSubject.next(changeEvent.id);
  }

  protected handlePayloadUpdate(existingRecord, changeEvent) {
    if (existingRecord === null || existingRecord === undefined) {
    } else {
      // console.log("updating existing entity in data store");
      this.dataStore.entities[this.dataStore.entities.indexOf(existingRecord)] =
        Object.assign(
          this.dataStore.entities[
            this.dataStore.entities.indexOf(existingRecord)
          ],
          changeEvent.payload
        );
      this._entitiesSubject.next(Object.assign({}, this.dataStore).entities);
    }
  }

  protected handleUpdate(changeEvent) {
    if (this.streamSingleUpdates && changeEvent.payload) {
      this._entitySubject.next(changeEvent.payload);
      return;
    }
    const existingRecord = this.dataStore.entities.find((x) => {
      return x["id"] === changeEvent.id;
    });
    if (!existingRecord) {
      // this.handleInsert(changeEvent);
    }
    if (changeEvent.payload) {
      this.handlePayloadUpdate(existingRecord, changeEvent);
    } else {
      this.restService
        // .getBySelfLink(changeEvent.href + '?projection=detail')
        .getResource(changeEvent.id, { params: { projection: "detail" } })
        .subscribe((resource) => {
          console.log(JSON.stringify(resource));
          if (existingRecord === null || existingRecord === undefined) {
            // this._entitiesSubject.next(
            //   Object.assign({}, this.dataStore).entities
            // );
          } else {
            this.dataStore.entities[
              this.dataStore.entities.indexOf(existingRecord)
            ] = Object.assign(
              this.dataStore.entities[
                this.dataStore.entities.indexOf(existingRecord)
              ],
              resource
            );
            resource =
              this.dataStore.entities[
                this.dataStore.entities.indexOf(existingRecord)
              ];
          }
          this._entitiesSubject.next([resource]);
        });
    }
  }

  protected subscribeToMessaging(
    entityName: string,
    streamSingleUpdates = false
  ) {
    // console.log("Subscribing to: " + entityName);
    this.streamSingleUpdates = streamSingleUpdates;
    this.stompEntitySubscriber
      .subscribeToEntity(entityName)
      .subscribe((changeEvent) => {
        if (changeEvent !== null) {
          switch (changeEvent.action) {
            case EntityChangeType.INSERT:
              this.handleInsert(changeEvent);
              break;
            case EntityChangeType.DELETE:
              this.handleDelete(changeEvent);
              break;
            case EntityChangeType.UPDATE:
              this.handleUpdate(changeEvent);
              break;
          }
        }
      });
  }

  private removeLinks<T>(entity: T): T {
    const { _links, _remoteSortIndex, ...entityWithoutLinks } = entity as any;
    return entityWithoutLinks;
  }
}
